/**
 * Folders — the workspace-scoped set a writer organises the homepage by.
 *
 * The other KIND of `document_groups` record (see `documentGroups.ts` for
 * submissions). A folder holds documents (`DocumentSessionDoc.folder_id`) and
 * submissions (`parent_group_id`). Filing is strictly per-document: a leaf
 * carries its own `folder_id` like any other document, and is never
 * re-parented through its submission. A folder is organisation, never
 * permission — filing grants nothing.
 *
 * One client for both surfaces, so what Word sends and what the web sends is
 * the same request. Membership goes through the seams that already exist:
 * `PUT /document-sessions/{id}` for a document (`moveDocumentToFolder`),
 * `PATCH /document-groups/{id}` for a submission (`updateDocumentGroup`).
 *
 * @see server/routes/folders.py
 */

import { type ClientConfig, request } from "./http";

export const FOLDERS_PATH = "/folders";

export interface FolderSubmission {
  id: string;
  label: string;
  parentGroupId: string | null;
}

export interface Folder {
  id: string;
  label: string;
  workspaceId: string | null;
  createdBy: string | null;
  /** The submissions filed into this folder. Documents are NOT here — they
   *  arrive through the run listing, the one place visibility is decided. */
  submissions: FolderSubmission[];
}

interface WireSubmission {
  id: string;
  label: string;
  parent_group_id?: string | null;
}

interface WireFolder {
  id: string;
  label: string;
  workspace_id?: string | null;
  created_by?: string | null;
  submissions?: WireSubmission[];
}

function fromWire(f: WireFolder): Folder {
  return {
    id: f.id,
    label: f.label,
    workspaceId: f.workspace_id ?? null,
    createdBy: f.created_by ?? null,
    submissions: (f.submissions ?? []).map((s) => ({
      id: s.id,
      label: s.label,
      parentGroupId: s.parent_group_id ?? null,
    })),
  };
}

/** The folders of these workspaces, most recently used first. Asks nothing
 *  for an empty list — there is nothing to ask for. */
export async function listFolders(cfg: ClientConfig, workspaceIds: readonly string[]): Promise<Folder[]> {
  const ids = workspaceIds.filter((id) => id.length > 0);
  if (ids.length === 0) return [];
  const qs = new URLSearchParams();
  for (const id of ids) qs.append("workspace_id", id);
  const body = await request<{ folders?: WireFolder[] } | undefined>(cfg, "GET", `${FOLDERS_PATH}?${qs.toString()}`);
  return (body?.folders ?? []).map(fromWire);
}

/** "New folder…" — mint one. Creating it files nothing into it. */
export async function createFolder(cfg: ClientConfig, args: { label: string; workspaceId: string }): Promise<Folder> {
  return fromWire(
    await request<WireFolder>(cfg, "POST", FOLDERS_PATH, { label: args.label, workspace_id: args.workspaceId }),
  );
}

/** One record; no member is touched. */
export async function renameFolder(cfg: ClientConfig, folderId: string, label: string): Promise<Folder> {
  return fromWire(await request<WireFolder>(cfg, "PATCH", `${FOLDERS_PATH}/${encodeURIComponent(folderId)}`, { label }));
}

/** Deletes the folder and UNFILES what was in it; deletes no document. */
export async function deleteFolder(cfg: ClientConfig, folderId: string): Promise<void> {
  await request<undefined>(cfg, "DELETE", `${FOLDERS_PATH}/${encodeURIComponent(folderId)}`);
}

/**
 * File a document into a folder, or unfile it with `null`. The session write
 * is the membership seam, and it is strictly PER-DOCUMENT: a leaf takes
 * `folder_id` exactly as any other document does, so callers offer this on
 * every card. Unfiling sends `clear_fields`, not a null — the server only
 * persists a folder when the write names one.
 */
export async function moveDocumentToFolder(cfg: ClientConfig, docId: string, folderId: string | null): Promise<void> {
  const body = folderId === null ? { clear_fields: ["folder_id"] } : { folder_id: folderId };
  await request<unknown>(cfg, "PUT", `/document-sessions/${encodeURIComponent(docId)}`, body);
}
